import { Message } from '@/components/message'
import { useRequest } from 'ahooks'
import { getTaskGroups } from '@/api/account-center/task-management'
import { t } from '@/i18n'
import {
  getChainInfo,
  GetChainInfoRequest,
  getAgent,
  getTemplateList,
  GetTemplateList,
  getCaseList,
  GetCaseList,
  saveCaseToTemplate,
  SaveCaseToTemplate,
  getCaseDetail,
  GetCaseDetail,
  getAllTemplateId,
  getAllCaseId,
  getAttackTypeList,
  getResourceTagList,
  getResourceIdsByTemplateIds,
  tpCreateTask,
  getEmail,
} from '@/api/threat-protection/create-task'

// 获取任务分组
export const useTaskGroup = () => {
  const { runAsync: run, data } = useRequest(params => getTaskGroups(params), {
    manual: true,
    onError: (error: any) => {
      Message.error(error.msg || t('获取分组列表失败'))
    },
  })

  return [data?.data?.groups || [], run] as const
}

// agent
export const useGetAgent = () => {
  const { data, runAsync, loading } = useRequest(() => getAgent(), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取agent列表失败'))
    },
  })
  return [data?.data?.items || [], runAsync, loading] as const
}

// 链路信息
export const useGetChainInfo = () => {
  const { data, runAsync, loading } = useRequest(
    (params: GetChainInfoRequest) => getChainInfo(params),
    {
      manual: true,
      onError(err: any) {
        Message.error(err?.msg || t('获取链路信息失败'))
      },
    },
  )
  return [data?.data?.chainDeviceList || [], runAsync, loading] as const
}

// 获取模板列表
export const useGetTemplateList = () => {
  const { runAsync, loading } = useRequest((params: GetTemplateList) => getTemplateList(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取模板列表失败'))
    },
  })
  return [runAsync, loading] as const
}

// 获取关联用例列表
export const useGetCaseList = () => {
  const { runAsync, loading } = useRequest((params: GetCaseList) => getCaseList(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取关联用例列表失败'))
    },
  })
  return [runAsync, loading] as const
}

// 保存已选择用例为模板
export const useSaveCaseToTemplate = () => {
  const { runAsync, loading } = useRequest(
    (params: SaveCaseToTemplate) => saveCaseToTemplate(params),
    {
      manual: true,
      onError(err: any) {
        Message.error(err?.msg || t('保存模板失败'))
      },
    },
  )
  return [runAsync, loading] as const
}

// 获取用例详情
export const useGetCaseDetail = () => {
  const { data, runAsync, loading } = useRequest((params: GetCaseDetail) => getCaseDetail(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取用例详情失败'))
    },
  })
  return [data?.data || {}, runAsync, loading] as const
}

// 获取所有模板id
export const useGetAllTemplateId = () => {
  const { data, runAsync } = useRequest((params: GetTemplateList) => getAllTemplateId(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取所有模板id失败'))
    },
  })
  return [runAsync] as const
}

// 获取所有用例id
export const useGetAllCaseId = () => {
  const { data, runAsync } = useRequest((params: GetCaseList) => getAllCaseId(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取所有用例id失败'))
    },
  })
  return [runAsync] as const
}

export const schedulerTypeMap = {
  scheduler_type_immediate: t('即时任务'),
  scheduler_type_reserve: t('定时任务'),
  scheduler_type_daily: t('每天'),
  scheduler_type_weekly: t('每周'),
  scheduler_type_monthly: t('每月'),
  scheduler_type_hourly: t('每小时'),
  scheduler_type_minutely: t('每分钟'),
}

// 获取攻击类型

export const useAttackTypeList = () => {
  const { data, runAsync } = useRequest(params => getAttackTypeList(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取攻击类型失败'))
    },
  })
  return [data?.data?.attackTypeFilters || [], runAsync] as const
}

// 获取用例标签

export const useResourceTagList = () => {
  const { data, runAsync } = useRequest(params => getResourceTagList(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取用例标签失败'))
    },
  })
  return [data?.data?.categoryList || [], runAsync] as const
}

// 根据templateIds获取resourceIds
export const useGetResourceIdsByTemplateIds = () => {
  const { data, runAsync } = useRequest(params => getResourceIdsByTemplateIds(params), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('获取资源id失败'))
    },
  })
  return [data?.data?.resourceIds || [], runAsync] as const
}

/**
 * 创建任务
 */
export interface TpCreateTask {
  agentToAgent?: TaskAgentToAgent
  /**
   * int32 waiting_time = 9;
   * repeated string template_ids = 10;
   */
  complishCondition?: string
  cronSchedule?: BasthreatProtectionv1TaskCronSchedule
  /**
   * 安全设备ID列表
   */
  deviceIds?: string[]
  /**
   * 任务执行次数限制
   */
  executionTimes?: number
  /**
   * 任务分组ID
   */
  groupId?: string
  name?: string
  /**
   * PCAP收集
   */
  pcapCollection?: boolean
  /**
   * 优先级
   */
  priority?: number
  reportConfig?: TaskReportConfig
  /**
   * 用例ID列表
   */
  resourceIds?: string[]
  scenarioExecutionActionId?: string
  schedulerType?: BasthreatProtectionv1TaskSchedulerType
  taskKind?: string
  taskTime?: string
  /**
   * 模板列表
   */
  templateList?: BasthreatProtectionv1CreateTaskRequestTemplate[]
  /**
   * string days = 5;
   * string day_of_week = 6;
   * repeated string plugin_ids = 7;
   */
  userId?: string
  [property: string]: any
}

/**
 * 验证信息
 */
export interface TaskAgentToAgent {
  /**
   * dnat
   */
  dNat?: string
  /**
   * 攻击目标
   */
  dstAgentId?: string
  /**
   * 中间经过 IP
   */
  middleIps?: string[]
  /**
   * snat
   */
  sNat?: string
  /**
   * 攻击源
   */
  srcAgentId?: string
  [property: string]: any
}

/**
 * basthreat_protectionv1TaskCronSchedule
 */
export interface BasthreatProtectionv1TaskCronSchedule {
  /**
   * 每月的哪几天
   */
  dayOfMonth?: number[]
  /**
   * 周期时间，每周的哪几天
   */
  dayOfWeek?: number[]
  /**
   * 间隔N小时
   */
  intervalOfHour?: number
  /**
   * 间隔N分钟
   */
  intervalOfMinute?: number
  /**
   * (每日/每周/每月)当天的时间 hh:ii:ss
   */
  timeOfDay?: string
  [property: string]: any
}

/**
 * TaskReportConfig
 */
export interface TaskReportConfig {
  condition?: ReportConfigCondition
  /**
   * 邮箱
   */
  emailList?: ReportConfigEmail[]
  [property: string]: any
}

/**
 * - condition_anyway: 无条件
 * - condition_all_failed: 全部拦截失败
 * - condition_exist_failed: 存在拦截失败
 */
export enum ReportConfigCondition {
  ConditionAllFailed = 'condition_all_failed',
  ConditionAnyway = 'condition_anyway',
  ConditionExistFailed = 'condition_exist_failed',
}

/**
 * ReportConfigEmail
 */
export interface ReportConfigEmail {
  /**
   * 人员邮箱
   */
  email?: string
  /**
   * 人员名称
   */
  name?: string
  [property: string]: any
}

/**
 * - scheduler_type_immediate: 即时任务
 * - scheduler_type_reserve: 定时任务
 * - scheduler_type_daily: 每日任务
 * - scheduler_type_weekly: 周任务
 * - scheduler_type_monthly: 月任务
 * - scheduler_type_hourly: 每隔N小时任务
 * - scheduler_type_minutely: 每隔N分钟任务
 */
export enum BasthreatProtectionv1TaskSchedulerType {
  SchedulerTypeDaily = 'scheduler_type_daily',
  SchedulerTypeHourly = 'scheduler_type_hourly',
  SchedulerTypeImmediate = 'scheduler_type_immediate',
  SchedulerTypeMinutely = 'scheduler_type_minutely',
  SchedulerTypeMonthly = 'scheduler_type_monthly',
  SchedulerTypeReserve = 'scheduler_type_reserve',
  SchedulerTypeUnknown = 'scheduler_type_unknown',
  SchedulerTypeWeekly = 'scheduler_type_weekly',
}

/**
 * basthreat_protectionv1CreateTaskRequestTemplate
 */
export interface BasthreatProtectionv1CreateTaskRequestTemplate {
  /**
   * 模板ID
   */
  id?: string
  /**
   * 使用最新的用例
   */
  useLatestResource?: boolean
  [property: string]: any
}

export const useTpCreateTask = () => {
  const { runAsync, loading } = useRequest((data: TpCreateTask) => tpCreateTask(data), {
    manual: true,
    onError(err: any) {
      Message.error(err?.msg || t('创建任务失败'))
    },
  })
  return [runAsync, loading] as const
}

export const useGetEmail = () => {
  const { data } = useRequest(() => getEmail(), {
    manual: false,
    onError(err: any) {
      Message.error(err?.msg || t('获取邮箱列表失败'))
    },
  })
  return [data?.data?.emailUsers || []] as const
}
